/**
 * Canonical Event Factory
 * 
 * SINGLE SOURCE OF TRUTH for all system event creation.
 * All event emitters across the platform must use this factory.
 * Handles ID generation, timestamp normalization, and validation.
 */

import type { SystemEvent, SystemEventType } from "./communicationEngine";

let eventCounter = 0;

function generateEventId(): string {
  eventCounter++;
  return `evt-${Date.now()}-${eventCounter}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Create a canonical system event with normalized defaults.
 * This is the ONLY way to create SystemEvents in the platform.
 */
export function createSystemEvent(params: {
  type: SystemEventType;
  jobId: string;
  payload?: Record<string, any>;
  triggeredBy?: string;
  timestamp?: string;
}): SystemEvent {
  const { type, jobId, payload = {}, triggeredBy = "System", timestamp } = params;

  if (!type) throw new Error("Event type is required");
  if (!jobId) throw new Error("Event jobId is required");

  return {
    type,
    jobId,
    timestamp: timestamp || new Date().toISOString(),
    payload: {
      ...payload,
      triggeredBy,
      eventId: generateEventId(),
    },
  };
}

/**
 * Create multiple events from a batch of params.
 * Useful for state diff detection that yields multiple events.
 */
export function createSystemEvents(
  batch: Array<{
    type: SystemEventType;
    jobId: string;
    payload?: Record<string, any>;
    triggeredBy?: string;
    timestamp?: string;
  }>
): SystemEvent[] {
  return batch.map(createSystemEvent);
}
